import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

/**
 * Get all available integrations
 */
export const getIntegrations = async () => {
    try {
        console.log('[IntegrationAPI] Fetching integrations from:', `${API_BASE_URL}/api/integrations`);
        const response = await apiClient.get(`${API_BASE_URL}/api/integrations`);
        console.log('[IntegrationAPI] Response status:', response.status);
        console.log('[IntegrationAPI] Response data:', response.data);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error fetching integrations:', error);
        console.error('[IntegrationAPI] Error response:', error.response?.data);
        console.error('[IntegrationAPI] Error status:', error.response?.status);
        throw error;
    }
};

/**
 * Get integration by ID
 */
export const getIntegration = async (integrationId) => {
    try {
        const response = await apiClient.get(`${API_BASE_URL}/api/integrations/${integrationId}`);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error fetching integration:', error);
        throw error;
    }
};

/**
 * Get connected integrations for a subteam
 */
export const getSubteamIntegrations = async (subteamId) => {
    try {
        const response = await apiClient.get(`${API_BASE_URL}/api/subteams/${subteamId}/integrations`);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error fetching subteam integrations:', error);
        throw error;
    }
};

/**
 * Connect integration to subteam
 * For OAuth: returns authUrl to redirect to
 * For API key: connects directly
 */
export const connectIntegration = async (subteamId, integrationId, connectionData = {}, settings = {}) => {
    try {
        const response = await apiClient.post(
            `${API_BASE_URL}/api/subteams/${subteamId}/integrations/${integrationId}/connect`,
            { connectionData, settings }
        );
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error connecting integration:', error);
        throw error;
    }
};

/**
 * Disconnect integration from subteam
 */
export const disconnectIntegration = async (subteamId, integrationId) => {
    try {
        const response = await apiClient.delete(`${API_BASE_URL}/api/subteams/${subteamId}/integrations/${integrationId}`);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error disconnecting integration:', error);
        throw error;
    }
};

/**
 * Update integration settings
 */
export const updateIntegrationSettings = async (subteamId, integrationId, settings) => {
    try {
        const response = await apiClient.put(
            `${API_BASE_URL}/api/subteams/${subteamId}/integrations/${integrationId}/settings`,
            { settings }
        );
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error updating integration settings:', error);
        throw error;
    }
};

/**
 * Sync integration (trigger manual sync)
 */
export const syncIntegration = async (subteamId, integrationId) => {
    try {
        const response = await apiClient.post(`${API_BASE_URL}/api/subteams/${subteamId}/integrations/${integrationId}/sync`, {});
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error syncing integration:', error);
        throw error;
    }
};

/**
 * Get connected integrations for a team
 */
export const getTeamIntegrations = async (teamId) => {
    try {
        const response = await apiClient.get(`${API_BASE_URL}/api/teams/${teamId}/integrations`);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error fetching team integrations:', error);
        throw error;
    }
};

/**
 * Connect integration to team
 * For OAuth: returns authUrl to redirect to
 * For API key: connects directly
 */
export const connectTeamIntegration = async (teamId, integrationId, connectionData = {}, settings = {}) => {
    try {
        const response = await apiClient.post(
            `${API_BASE_URL}/api/teams/${teamId}/integrations/${integrationId}/connect`,
            { connectionData, settings }
        );
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error connecting team integration:', error);
        throw error;
    }
};

/**
 * Disconnect integration from team
 */
export const disconnectTeamIntegration = async (teamId, integrationId) => {
    try {
        const response = await apiClient.delete(`${API_BASE_URL}/api/teams/${teamId}/integrations/${integrationId}`);
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error disconnecting team integration:', error);
        throw error;
    }
};

/**
 * Update team integration settings
 */
export const updateTeamIntegrationSettings = async (teamId, integrationId, settings) => {
    try {
        const response = await apiClient.put(
            `${API_BASE_URL}/api/teams/${teamId}/integrations/${integrationId}/settings`,
            { settings }
        );
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error updating team integration settings:', error);
        throw error;
    }
};

/**
 * Sync team integration (trigger manual sync)
 */
export const syncTeamIntegration = async (teamId, integrationId) => {
    try {
        const response = await apiClient.post(`${API_BASE_URL}/api/teams/${teamId}/integrations/${integrationId}/sync`, {});
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error syncing team integration:', error);
        throw error;
    }
};

/**
 * Get Azure AD groups for team integration setup
 */
export const getTeamIntegrationGroups = async (teamId, integrationId, search = '') => {
    try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        const query = params.toString();
        const response = await apiClient.get(
            `${API_BASE_URL}/api/teams/${teamId}/integrations/${integrationId}/groups${query ? `?${query}` : ''}`
        );
        return response.data;
    } catch (error) {
        console.error('[IntegrationAPI] Error fetching Azure AD groups:', error);
        throw error;
    }
};

